import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';

// Import all English translations as defaults (source of truth)
import enCommon from './locales/en/common.json';
import enLanding from './locales/en/landing.json';
import enCatalog from './locales/en/catalog.json';
import enDashboard from './locales/en/dashboard.json';
import enAuth from './locales/en/auth.json';
import enCart from './locales/en/cart.json';
import enNotifications from './locales/en/notifications.json';

// Import Spanish translations
import esCommon from './locales/es/common.json';
import esLanding from './locales/es/landing.json';
import esCatalog from './locales/es/catalog.json';
import esDashboard from './locales/es/dashboard.json';
import esAuth from './locales/es/auth.json';
import esCart from './locales/es/cart.json';
import esNotifications from './locales/es/notifications.json';

// Import Catalan translations
import caCommon from './locales/ca/common.json';
import caLanding from './locales/ca/landing.json';
import caCatalog from './locales/ca/catalog.json';
import caDashboard from './locales/ca/dashboard.json';
import caAuth from './locales/ca/auth.json';
import caCart from './locales/ca/cart.json';
import caNotifications from './locales/ca/notifications.json';

export const defaultNS = 'common';
export const supportedLanguages = ['en', 'es', 'ca'] as const;
export type SupportedLanguage = (typeof supportedLanguages)[number];

export const languageNames: Record<SupportedLanguage, string> = {
  en: 'English',
  es: 'Espanol',
  ca: 'Catala',
};

// Resources bundled with the app
const resources = {
  en: {
    common: enCommon,
    landing: enLanding,
    catalog: enCatalog,
    dashboard: enDashboard,
    auth: enAuth,
    cart: enCart,
    notifications: enNotifications,
  },
  es: {
    common: esCommon,
    landing: esLanding,
    catalog: esCatalog,
    dashboard: esDashboard,
    auth: esAuth,
    cart: esCart,
    notifications: esNotifications,
  },
  ca: {
    common: caCommon,
    landing: caLanding,
    catalog: caCatalog,
    dashboard: caDashboard,
    auth: caAuth,
    cart: caCart,
    notifications: caNotifications,
  },
};

const isDevelopment = import.meta.env.DEV;

void i18n
  // Use http backend for lazy loading in production (optional)
  .use(HttpBackend)
  // Detect user language
  .use(LanguageDetector)
  // Pass i18n instance to react-i18next
  .use(initReactI18next)
  // Initialize i18next
  .init({
    resources,
    fallbackLng: 'en',
    defaultNS,
    ns: ['common', 'landing', 'catalog', 'dashboard', 'auth', 'cart', 'notifications'],
    
    // Language detection configuration
    detection: {
      // Order of language detection methods
      order: ['localStorage', 'navigator', 'htmlTag'],
      // Cache user language selection in localStorage
      caches: ['localStorage'],
      // Key to use in localStorage
      lookupLocalStorage: 'brickmod-language',
    },
    
    // Interpolation configuration
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    
    // Enable debug mode in development
    debug: isDevelopment,
    
    // Lazy loading backend configuration (for future use)
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },
    
    // React specific options
    react: {
      useSuspense: true,
    },
  });

// Update HTML lang attribute when language changes
i18n.on('languageChanged', (lng) => {
  document.documentElement.setAttribute('lang', lng);
});

// Set initial HTML lang attribute
if (typeof document !== 'undefined') {
  document.documentElement.setAttribute('lang', i18n.language);
}

export default i18n;
